import { ChevronDown, InformationSquare, Logout, Notification } from '@carbon/icons-react';
import { Avatar, Button, ClickAwayListener, Divider, FormControl, Menu, MenuItem, Select, useTheme } from '@mui/material';
import Fade from '@mui/material/Fade';
import Tooltip from '@mui/material/Tooltip';
import MenuIcon from '@mui/icons-material/Menu';
import i18next, { t } from 'i18next';
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import bgDark from '../../assets/img/vier-globalheader-background.svg';
import bgLight from '../../assets/img/vier-globalheader-background.svg';
import { LoginSettings } from '../../models/LoginSettings';
import { EditorModeEnum } from '../../models/EditorModeEnum';
import { RoleEnum } from '../../models/RoleEnum';
import { StageViewModeEnum } from '../../models/StageViewModeEnum';
import SettingsService from '../../services/settingsService/SettingsService';
import StateService from '../../services/stateService/StateService';
import UserService from '../../services/userService/UserService';
import RoleChange from '../role-change/RoleChange';
import UpdateNotification from '../update-notification/UpdateNotification';
import { useStyles } from './styles';
import ThemeToggle from './themeToggle/ThemeToggle';
import pj from '../../../package.json';

const GlobalHeader = () => {
    const [themeName, setThemeName] = useState('');
    const [anchorEl, setAnchorEl] = useState(null);
    const [anchorEl1, setAnchorEl1] = useState(null);
    const [event, setEvent] = useState(false);
    const [initials, setInitials] = useState('');
    const [currentRoles, setCurrentRoles] = useState([] as RoleEnum[]);
    const [menuOpen, setMenuOpen] = useState(false);
    const [menuOpen1, setMenuOpen1] = useState(false);
    const [showNavigation, setShowNavigation] = useState(false);
    const classes = useStyles();
    const getContentAnchorEl = null;
    const getContentAnchorEl1 = null;
    const bodyTheme = localStorage.getItem('theme') || 'light'; // retrieve the value from local storage, default to 'light'
    const [stageViewMode, setStageViewMode] = useState(StageViewModeEnum.PUBLISHED as StageViewModeEnum);
    const [currentLanguage, setCurrentLanguage] = useState(i18next.language);
    const [newLanguage, setNewLanguage] = useState(i18next.language);
    const [isLoginSettingsSaving, setIsLoginSettingsSaving] = useState(false);
    const [loginSettings, setLoginSettings] = useState({} as LoginSettings);
    const [currentDuration, setCurrentDuration] = useState(300000);
    const [currentCustomerUuid, setCurrentCustomerUuid] = useState('');
    const abortController = new AbortController();
    const [latestDuration, setLatestDuration] = useState(loginSettings.duration || 300000);
    const localStorageLang = localStorage.getItem('language') || 'de';
    const [fullname, setFullname] = useState('');
    const [greeting, setGreeting] = useState('');
    const [show, setShow] = useState(false);
    const theme = useTheme();
    const [langArray, setLangArray] = useState(['de', 'en']);
    const background = {
        local: '#7FBF7F',
        development: '#F57316',
        staging: '#add8e6',
    };
    const [globalHeaderBg, setGlobalHeaderBg] = useState(background.local);

    /* subscribe to global state */
    useEffect(() => {
        const darkTheme$ = StateService.darkTheme$.subscribe((theme) => {
            setThemeName(theme);
        });

        const stageViewMode$ = StateService.stageViewMode$.subscribe((mode) => {
            setStageViewMode(mode);
        });

        const openNavigation$ = StateService.openNavigation$.subscribe(() => { });

        const loginSettings$ = StateService.loginSettings$.subscribe((loginSettings) => {
            setLoginSettings(loginSettings);
        });

        const currentCustomerUuid$ = StateService.currentCustomerUuid$.subscribe((currentCustomerUuid) => {
            setCurrentCustomerUuid(currentCustomerUuid);
        });

        const currentRoles$ = StateService.currentRoles$.subscribe((currentRoles) => {
            setCurrentRoles(currentRoles);
        });

        return () => {
            darkTheme$.unsubscribe();
            openNavigation$.unsubscribe();
            stageViewMode$.unsubscribe();
            loginSettings$.unsubscribe();
            currentCustomerUuid$.unsubscribe();
            currentRoles$.unsubscribe();
        };
    });

    useEffect(() => {
        // Set initials
        /*setInitials('MM');*/
        const firstChar = getFirstChar(UserService.getGivenName());
        const secondChar = getFirstChar(UserService.getFamilyName());
        if (UserService.getGivenName() !== UserService.getFamilyName() && firstChar.length > 0 && secondChar.length > 0) {
            // 1. Check by given and family name
            setInitials(`${firstChar}${secondChar}`);
            setFullname(`${UserService.getGivenName} ${UserService.getFamilyName}`);
            setGreeting(`Guten Tag ${fullname}! Schön, dass Sie da sind!`);
        } else if (UserService.getGivenName() === UserService.getFamilyName() && firstChar.length > 0 && secondChar.length > 0) {
            // 2. If given and family name are the same use first char of given name
            setInitials(`${firstChar}`);
            setFullname(`${UserService.getGivenName} ${UserService.getFamilyName}`);
            setGreeting(`Guten Tag ${fullname}! Schön, dass Sie da sind!`);
        } else if (UserService.getMail()) {
            // 3. Check by given mail
            const splittedAdress = UserService.getMail()?.split('@');
            const splittedPrefix = splittedAdress[0].split('.');
            const initials = splittedPrefix[1]
                ? `${getFirstChar(splittedPrefix[0])}${getFirstChar(splittedPrefix[1])}`
                : `${splittedPrefix[0]?.substring(0, 2)}`;
            setInitials(initials);
            setFullname(`${UserService.getGivenName} ${UserService.getFamilyName}`);
            setGreeting(`Guten Tag ${fullname}! Schön, dass Sie da sind!`);
        } else {
            // 4. Show nothing/user avatar
            setInitials(null);
            setFullname(null);
        }
    }, []);

    useEffect(() => {
        if (loginSettings.language) {
            setCurrentLanguage(newLanguage);
        }
        // eslint-disable-next-line
    }, [loginSettings]);

    useEffect(() => {
        if (loginSettings.language) {
            setLoginSettings({ ...loginSettings, language: currentLanguage, duration: currentDuration });
        }
        // eslint-disable-next-line
    }, [currentLanguage]);

    useEffect(() => {
        StateService.openNavigation$.next(showNavigation);
    }, [showNavigation]);

    useEffect(() => {
        const hostname = window?.location?.hostname;
        const newLang = shouldIncludeKeys() ? [...langArray, 'keys'] : langArray;

        if (hostname.includes('localhost')) {
            setGlobalHeaderBg(background.local);
        } else if (hostname.includes('development')) {
            setGlobalHeaderBg(background.development);
        } else if (hostname.includes('staging')) {
            setGlobalHeaderBg(background.staging);
        } else {
            setGlobalHeaderBg(theme.palette.neutral[50]);
        }

        setLangArray(newLang);
    }, []);

    const getFirstChar = (value: string) => {
        return value?.charAt(0)?.toUpperCase() || '';
    };

    const handleClick = useCallback((event) => {
        setAnchorEl(event.currentTarget);
        setMenuOpen((prev) => !prev);
    }, []);

    const handleClose = () => {
        setAnchorEl(null);
        setAnchorEl1(null);
        setMenuOpen(false);
    };

    const openInternalLink = (adress: string) => {
        StateService.editorMode$.next(EditorModeEnum.CLOSED);
        StateService.selectedRoute$.next(adress);
    };

    const openHomepage = () => {
        openInternalLink('/');
    };

    const setDarkMode = () => {
        document.querySelector('body').setAttribute('data-theme', 'dark');
        StateService.darkTheme$.next('dark');
        localStorage.setItem('theme', 'dark'); // store the value in local storage
    };
    const setLightMode = () => {
        document.querySelector('body').setAttribute('data-theme', 'light');
        StateService.darkTheme$.next('light');
        localStorage.setItem('theme', 'light'); // store the value in local storage
    };

    const toggleTheme = (e: any) => {
        const checked = e?.target?.checked;
        checked ? setLightMode() : setDarkMode();
        setEvent(e);
        checked && localStorage.setItem('theme', 'light');
    };

    const handleToggleNavigation = (event) => {
        setShowNavigation((prevShowNavigation) => !prevShowNavigation);
    };

    const openSettings = () => {
        StateService.editorMode$.next(EditorModeEnum.SETTINGS);
    };

    /* has to be placed here, because of above function */
    const avatarMenuItems = [
        /*{
            icon: Settings,
            id: 0,
            label: t('COMMON.SETTINGS_LABEL'),
            'data-testid': 'settings',
            onClick: openSettings,
        },*/
        {
            icon: Logout,
            id: 1,
            label: t('NAVIGATION.LOGOUT_LABEL'),
            onClick: UserService.doLogout,
        },
        {
            icon: InformationSquare,
            id: 2,
            label: t(`CUSTOMER_SETTINGS.VERSION_NUMBER_LABEL`) + ` ${pj.version}`,
        },
    ];

    const getRoleSwitchButton = useMemo(() => {
        const email = UserService.getMail();
        const pattern = /(4com|vier\.ai)/;
        const hasSubstring = pattern.test(email);
        if (hasSubstring) {
            return (
                <div className={classes.roleSwitcher} data-testid="role-change">
                    <RoleChange />
                </div>
            );
        } else {
            return <div data-testid="role-change" />; // Render something else or nothing
        }
    }, []);

    const handleLanguageChange = (event: any) => {
        const value = event.target.value as string;
        i18next.changeLanguage(value);
        saveLoginSettings(value);
    };

    async function saveLoginSettings(savedLanguage: string) {
        localStorage.setItem('language', savedLanguage);
        setCurrentLanguage(savedLanguage);
        setNewLanguage(savedLanguage);
        setIsLoginSettingsSaving(true);
        const response = await SettingsService.patchLoginSettings(
            { ...loginSettings, language: savedLanguage, duration: currentDuration },
            currentCustomerUuid,
            UserService.getUsername(),
            {
                signal: abortController.signal,
            },
        );
        if (response?.status === 204) {
            StateService.loginSettings$.next(loginSettings);
        }
        setIsLoginSettingsSaving(false);
        setLatestDuration(currentDuration);
    }

    const handleOpenNotif = (event: any) => {
        setAnchorEl1(event.currentTarget);
        setMenuOpen1((prevMenuOpen) => !prevMenuOpen);
    };

    const shouldIncludeKeys = () => {
        const url = window?.location?.href; // Gets the current URL
        return url.includes('localhost') || url.includes('development') || url.includes('staging');
    };

    return (
        <div
            className={classes.globalHeaderWrapper}
            style={{
                backgroundColor: globalHeaderBg,
                backgroundImage: bodyTheme === 'light' ? `url('${bgLight}')` : `url('${bgDark}')`,
                display: [RoleEnum.VIEWER].includes(currentRoles[0]) ? 'none' : 'block',
            }}
            data-testid="global-header-wrapper"
        >
            <div className={classes.globalHeader}>
                <div className={classes.menu_link_wrapper}>
                    {/* burger menu for mobile navigation */}
                    <div className={classes.globalHeader__burgerMenu}>
                        <MenuIcon onClick={handleToggleNavigation} data-testid="navigation-toggle" />
                    </div>
                    {/* vier logo */}
                    <div style={{ width: '100px' }}>
                        {/*uncomment until reseller logic is set*/}
                        {/*<a className={classes.globalHeader__logoLink} href="#" onClick={openHomepage}>
                            <img className={classes.globalHeader__logo} src={vierLogo} alt="VIER" />
                        </a>*/}
                    </div>
                </div>
                {/* give greeting a max width and elipse(...) */}
                <div className={classes.globalHeader__greeting}> {t('COMMON.GLOBAL_HEADER_GREETING').toString()} </div>
                <div className={classes.globalHeader__menuWrapper}>
                    {/* menu portal */}
                    {/* <div></div> */}
                    {/* notification bell */}
                    {/*<Tooltip
                        classes={ {
                            tooltip: classes.tooltip,
                        } }
                        title={ t( 'COMMON.GLOBAL_HEADER_BELL_TOOLTIP' ) }
                        placement="left-start"
                        TransitionComponent={ Fade }
                        data-testid="notification-button">
                        <div className={ classes.notifWrapper }>
                            <Notification onClick={ handleOpenNotif }/>
                            <Menu
                                classes={ { paper: classes.menu__wrapper } }
                                anchorEl={ anchorEl1 }
                                keepMounted
                                open={ Boolean( anchorEl1 ) }
                                onClose={ handleClose }
                                anchorOrigin={ {
                                    vertical: 60,
                                    horizontal: 20,
                                } }
                                transformOrigin={ {
                                    vertical: -10,
                                    horizontal: 'center',
                                } }
                                getContentAnchorEl={ getContentAnchorEl1 }
                                data-testid="menu"
                            >
                                <div>
                                    <UpdateNotification toggleDialog={ toggleDialog } handleClose={ handleClose }/>
                                </div>
                            </Menu>
                        </div>
                    </Tooltip>*/}
                    {/* avatar */}
                    <div>
                        <Avatar className={classes.avatar} onClick={handleClick} data-testid="global-header-avatar">
                            <span className={classes.lh1} style={{ marginTop: '2px' }}>
                                {initials}
                            </span>
                            {menuOpen && (
                                <Menu
                                    classes={{ paper: classes.menu__wrapper }}
                                    anchorEl={anchorEl}
                                    keepMounted
                                    open={Boolean(anchorEl)}
                                    //                                    onClose={handleClose}
                                    anchorOrigin={{
                                        vertical: 60,
                                        horizontal: -40,
                                    }}
                                    transformOrigin={{
                                        vertical: -10,
                                        horizontal: 'center',
                                    }}
                                    data-testid="menu"
                                >
                                    {/* role switch for vier employees */}
                                    {getRoleSwitchButton}
                                    {/* language switcher */}
                                    <FormControl classes={{ root: classes.languageSwitcher }}>
                                        <Select
                                            data-testid="language"
                                            value={localStorageLang}
                                            onChange={handleLanguageChange}
                                            inputProps={{
                                                name: 'language',
                                                id: 'language',
                                                style: { paddingLeft: 8, height: '32px !important' },
                                            }}
                                            IconComponent={ChevronDown}
                                        >
                                            {langArray.map((lang, i) => (
                                                <MenuItem value={lang} key={i} className={classes.dropDownOption}>
                                                    {t(`LANGUAGES.${lang.toUpperCase()}_DROPDOWN`).toString()}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    {avatarMenuItems.map((item) => (
                                        <div key={item.id}>
                                            {item.label === t(`CUSTOMER_SETTINGS.VERSION_NUMBER_LABEL`) + ` ${pj.version}` ? (
                                                <div
                                                    key={item.id}
                                                    className={`${classes.menuItems__version} classes.menu__${item.label}`}
                                                    style={{ width: '100%', fontSize: '16px' }}
                                                >
                                                    <div className={classes.item__version}>
                                                        {item.icon && <item.icon />}
                                                        <span>{item.label}</span>
                                                    </div>
                                                </div>
                                            ) : (
                                                <MenuItem onClick={handleClose} key={item.id} className={classes.menuItems}>
                                                    <div
                                                        className={`classes.menu__${item.label}`}
                                                        style={{ width: '100%', fontSize: '16px' }}
                                                    >
                                                        <div className={classes.item} onClick={() => item.onClick()}>
                                                            {item.icon && <item.icon />}
                                                            {item.label}
                                                        </div>
                                                    </div>
                                                </MenuItem>
                                            )}
                                        </div>
                                    ))}
                                    <Divider />
                                    <MenuItem className={classes.menuItems} data-testid="theme-toggle">
                                        <div className={classes.themeToggleHeadline}>
                                            {t('CUSTOMER_SETTINGS.THEME_TOGGLE_HEADLINE').toString()}{' '}
                                        </div>
                                        <ThemeToggle toggleTheme={toggleTheme} bodyTheme={bodyTheme} />
                                    </MenuItem>
                                </Menu>
                            )}
                        </Avatar>
                        {menuOpen && (
                            <Menu
                                classes={{ paper: classes.menu__wrapper }}
                                anchorEl={anchorEl}
                                keepMounted
                                open={Boolean(anchorEl)}
                                onClose={handleClose}
                                anchorOrigin={{
                                    vertical: 60,
                                    horizontal: -40,
                                }}
                                transformOrigin={{
                                    vertical: -10,
                                    horizontal: 'center',
                                }}
                                data-testid="menu"
                            >
                                {/* role switch for vier employees */}
                                {getRoleSwitchButton}
                                {/* language switcher */}
                                <FormControl classes={{ root: classes.languageSwitcher }}>
                                    <Select
                                        data-testid="language"
                                        value={localStorageLang}
                                        onChange={handleLanguageChange}
                                        inputProps={{
                                            name: 'language',
                                            id: 'language',
                                            style: { paddingLeft: 8, height: '32px !important' },
                                        }}
                                        IconComponent={ChevronDown}
                                    >
                                        {langArray.map((lang) => (
                                            <MenuItem value={lang} key={lang} className={classes.dropDownOption}>
                                                {t(`LANGUAGES.${lang.toUpperCase()}_DROPDOWN`).toString()}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                {avatarMenuItems.map((item) => (
                                    <div key={item.id}>
                                        {item.label === t(`CUSTOMER_SETTINGS.VERSION_NUMBER_LABEL`) + ` ${pj.version}` ? (
                                            <div
                                                key={item.id}
                                                className={`${classes.menuItems__version} classes.menu__${item.label}`}
                                                style={{ width: '100%', fontSize: '16px' }}
                                            >
                                                <div className={classes.item__version}>
                                                    {item.icon && <item.icon />}
                                                    <span>{item.label}</span>
                                                </div>
                                            </div>
                                        ) : (
                                            <MenuItem onClick={handleClose} key={item.id} className={classes.menuItems}>
                                                <div className={`classes.menu__${item.label}`} style={{ width: '100%', fontSize: '16px' }}>
                                                    <div className={classes.item} onClick={() => item.onClick()}>
                                                        {item.icon && <item.icon />}
                                                        {item.label}
                                                    </div>
                                                </div>
                                            </MenuItem>
                                        )}
                                    </div>
                                ))}
                                <Divider />
                                <MenuItem className={classes.menuItems} data-testid="theme-toggle">
                                    <div className={classes.themeToggleHeadline}>
                                        {t('CUSTOMER_SETTINGS.THEME_TOGGLE_HEADLINE').toString()}{' '}
                                    </div>
                                    <ThemeToggle toggleTheme={toggleTheme} bodyTheme={bodyTheme} />
                                </MenuItem>
                            </Menu>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default GlobalHeader;
